class CustomHeader extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.menus = this.querySelectorAll('.header-menu');
    this.menuButtons = this.querySelectorAll('.header-menu__button');
    this.sectionHeader = document.querySelector('.section-header');
  }

  closeAllOtherMenus(headerMenu) {
    this.menus.forEach((menu) => {
      if (menu !== headerMenu) {
        if (menu.close) menu.close();
        if (menu.tagName === 'DETAILS') {
          menu.removeAttribute('open');
          menu.toggleAttribute('open', false);
        }
        const menuContent = menu.querySelector('.header-menu__content');
        const menuDetails = menu.querySelectorAll('details');
        menuDetails.forEach((detailsElement) => detailsElement.removeAttribute('open'));
        menuContent?.classList.remove('active');
        menuContent?.setAttribute('aria-expanded', false);
      }
    });
    this.menuButtons.forEach((button) => {
      if (button !== headerMenu.button) {
        button.classList.remove('active');
      }
    });
  }
}

class StickyHeader extends CustomHeader {
  constructor() {
    super();
    this.header = document.querySelector('.section-header');
  }

  connectedCallback() {
    this.headerIsAlwaysSticky =
      this.getAttribute('data-sticky-type') === 'always' ||
      this.getAttribute('data-sticky-type') === 'reduce-logo-size';
    this.headerBounds = {};

    this.setHeaderHeight();

    window.matchMedia('(max-width: 990px)').addEventListener('change', this.setHeaderHeight.bind(this));

    if (this.headerIsAlwaysSticky) {
      this.header.classList.add('shopify-section-header-sticky');
    }

    this.currentScrollTop = 0;
    this.preventReveal = false;
    this.predictiveSearch = this.querySelector('predictive-search');

    this.onScrollHandler = this.onScroll.bind(this);
    this.hideHeaderOnScrollUp = () => (this.preventReveal = true);

    this.addEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
    window.addEventListener('scroll', this.onScrollHandler, false);

    const details = document.querySelectorAll('.menu-drawer__menu-item-details');
    if (details && details.length) {
      details.forEach((detailElement) => {
        const detailsOpenBtn = detailElement.querySelector('.open-button');
        detailElement.addEventListener('click', (e) => {
          e.stopPropagation();
          detailsOpenBtn?.classList.toggle('active');
        });
      });
    }

    this.createObserver();
  }

  setHeaderHeight() {
    document.documentElement.style.setProperty('--header-height', `${this.offsetHeight}px`);
  }

  disconnectedCallback() {
    this.removeEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
    window.removeEventListener('scroll', this.onScrollHandler);
  }

  createObserver() {
    let observer = new IntersectionObserver((entries, observer) => {
      this.headerBounds = entries[0].intersectionRect;
      observer.disconnect();
    });

    observer.observe(this.header);
  }

  onScroll() {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

    if (this.predictiveSearch && this.predictiveSearch.isOpen) return;

    if (scrollTop > this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
      this.header.classList.add('scrolled-past-header');
      if (this.preventHide) return;
      requestAnimationFrame(this.hide.bind(this));
    } else if (scrollTop < this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
      this.header.classList.add('scrolled-past-header');
      if (!this.preventReveal) {
        requestAnimationFrame(this.reveal.bind(this));
      } else {
        window.clearTimeout(this.isScrolling);

        this.isScrolling = setTimeout(() => {
          this.preventReveal = false;
        }, 66);

        requestAnimationFrame(this.hide.bind(this));
      }
    } else if (scrollTop <= this.headerBounds.top) {
      this.header.classList.remove('scrolled-past-header');
      requestAnimationFrame(this.reset.bind(this));
    }

    this.currentScrollTop = scrollTop;
  }

  hide() {
    if (this.headerIsAlwaysSticky) return;
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const height = this.getBoundingClientRect().height + 38;
    if (scrollTop >= height) {
      this.header.classList.add('shopify-section-header-hidden', 'shopify-section-header-sticky');
      this.closeMenuDisclosure();
    }
  }

  reveal() {
    if (this.headerIsAlwaysSticky) return;
    this.header.classList.add('shopify-section-header-sticky', 'animate');
    this.header.classList.remove('shopify-section-header-hidden');
  }

  reset() {
    if (this.headerIsAlwaysSticky) return;
    this.header.classList.remove('shopify-section-header-hidden', 'shopify-section-header-sticky', 'animate');
  }

  closeMenuDisclosure() {
    this.disclosures = this.disclosures || this.header.querySelectorAll('header-menu');
    this.disclosures.forEach((disclosure) => disclosure.close());
  }
}

if (!customElements.get('header-menu')) {
  customElements.define(
    'header-menu',
    class HeaderMenu extends HTMLElement {
      constructor() {
        super();
        this.header = document.querySelector('.header-wrapper');
        this.customHeader = document.querySelector('custom-header');
        this.elements = {
          button: this.querySelector('.header-menu__disclosure-button'),
          panel: this.querySelector('.disclosure__list-wrapper'),
        };
        this.exclusions = this.dataset.exclusions ? this.dataset.exclusions.split(' ') : [];
        this.addEventListener('keyup', this.onContainerKeyUp.bind(this));
        this.addEventListener('keydown', this.onContainerKeyDown.bind(this));
        this.addEventListener('focusout', this.closeSelector.bind(this));
        this.elements.button.addEventListener('click', this.openSelector.bind(this));

        if ('hover' in this.dataset) {
          this.elements.button.addEventListener('mouseover', () => this.openSelectorOnHover());
          this.addEventListener('mouseleave', (e) => this.closeSelector(e));
        }
      }

      hidePanel() {
        this.elements.button.setAttribute('aria-expanded', 'false');
        this.elements.panel.classList.add('menu-hidden');
        document.body.classList.remove('overflow-hidden-mobile');
        document.querySelector('.menu-drawer').classList.remove('selector-open');
        this.header.preventHide = false;
      }

      onContainerKeyDown(event) {
        const focusableItems = Array.from(this.querySelectorAll('a')).filter(
          (item) => !item.parentElement.classList.contains('menu-hidden')
        );
        let focusedItemIndex = focusableItems.findIndex((item) => item === document.activeElement);
        let itemToFocus;

        switch (event.code.toUpperCase()) {
          case 'ARROWUP':
            event.preventDefault();
            itemToFocus =
              focusedItemIndex > 0 ? focusableItems[focusedItemIndex - 1] : focusableItems[focusableItems.length - 1];
            itemToFocus.focus();
            break;
          case 'ARROWDOWN':
            event.preventDefault();
            itemToFocus =
              focusedItemIndex < focusableItems.length - 1 ? focusableItems[focusedItemIndex + 1] : focusableItems[0];
            itemToFocus.focus();
            break;
        }

        if (!this.elements.search) return;

        setTimeout(() => {
          focusedItemIndex = focusableItems.findIndex((item) => item === document.activeElement);
          if (focusedItemIndex > -1) {
            this.elements.search.setAttribute('aria-activedescendant', focusableItems[focusedItemIndex].id);
          } else {
            this.elements.search.setAttribute('aria-activedescendant', '');
          }
        });
      }

      onContainerKeyUp(event) {
        event.preventDefault();

        switch (event.code.toUpperCase()) {
          case 'ESCAPE':
            if (this.elements.button.getAttribute('aria-expanded') == 'false') return;
            this.hidePanel();
            event.stopPropagation();
            this.elements.button.focus();
            break;
          case 'SPACE':
            if (this.elements.button.getAttribute('aria-expanded') == 'true') return;
            this.openSelector();
            break;
        }
      }

      openSelector() {
        this.elements.button.focus();
        this.elements.panel.classList.toggle('menu-hidden');
        this.elements.button.setAttribute(
          'aria-expanded',
          (this.elements.button.getAttribute('aria-expanded') === 'false').toString()
        );
        if (!document.body.classList.contains('overflow-hidden-tablet')) {
          document.body.classList.add('overflow-hidden-mobile');
        }
        if (this.elements.search && this.mql.matches) {
          this.elements.search.focus();
        }
        if (this.hasAttribute('data-prevent-hide')) {
          this.header.preventHide = true;
        }
        document.querySelector('.menu-drawer').classList.add('selector-open');
      }

      openSelectorOnHover() {
        if (!this.elements.panel.classList.contains('menu-hidden')) return;
        this?.customHeader?.closeAllOtherMenus(this);
        this.elements.button.focus();
        this.elements.panel.classList.remove('menu-hidden');
        this.elements.button.setAttribute('aria-expanded', true);
        if (!document.body.classList.contains('overflow-hidden-tablet')) {
          document.body.classList.add('overflow-hidden-mobile');
        }
        if (this.elements.search && this.mql.matches) {
          this.elements.search.focus();
        }
        if (this.hasAttribute('data-prevent-hide')) {
          this.header.preventHide = true;
        }
        document.querySelector('.menu-drawer').classList.add('selector-open');
      }

      closeSelector(event) {
        if (event.type === 'mouseleave') {
          if (event.relatedTarget?.closest('header-menu') === this) return;
          if (this.contains(event.relatedTarget)) return;
          if (this.exclusions.some((className) => event.relatedTarget?.classList.contains(className))) return;
        }

        if (
          event.target.classList.contains('selector__overlay') ||
          !this.contains(event.target) ||
          !this.contains(event.relatedTarget)
        ) {
          this.hidePanel();
        }
      }

      close() {
        this.hidePanel();
      }
    }
  );
}
customElements.define('sticky-header', StickyHeader);
customElements.define('custom-header', CustomHeader);
